import {NetworkStatus} from '@apollo/client';
import React, {useMemo} from 'react';
import {
	GetRegulatoryDocumentDetailsQuery,
	useGetRegulatoryDocumentDetailsQuery,
} from '../hooks/useGetRegulatoryDocumentDetails.generated';
import {
	RegDocInjectedData,
	RegDocInjectedDataContext,
} from './RegDocInjectedDataContext';
import {mapWorkflowUserRefs} from '../regulatoryUtils';
import {UpdateGetexRegulationInput, WorkflowStatus} from 'types';

export interface InjectedDataResult {
	injectedData: RegDocInjectedData | null;
	isGetexInjected: boolean;
	isRegDocLoading: boolean;
	data: GetRegulatoryDocumentDetailsQuery | undefined;
	regDocNetworkStatus: NetworkStatus;
	getUpdateGetexInput: (id: string, data: any) => UpdateGetexRegulationInput;
}

export const useInjectedData = (
	docId: string,
	skip = false,
): InjectedDataResult => {
	const injectedData = React.useContext(RegDocInjectedDataContext);
	const isGetexInjected = injectedData !== null;

	const {
		loading: isRegDocLoadingFromQuery,
		data: dataFromQuery,
		networkStatus: regDocNetworkStatusFromQuery,
	} = useGetRegulatoryDocumentDetailsQuery({
		variables: {regulatoryDocumentId: docId},
		skip: isGetexInjected || skip || !docId,
		notifyOnNetworkStatusChange: true,
	});

	const isRegDocLoading = useMemo(
		() => (isGetexInjected ? (injectedData?.isLoading ?? false) : isRegDocLoadingFromQuery),
		[isGetexInjected, injectedData?.isLoading, isRegDocLoadingFromQuery],
	);

	const data = useMemo(
		() => (isGetexInjected ? injectedData?.data : dataFromQuery),
		[isGetexInjected, injectedData?.data, dataFromQuery],
	);

	const regDocNetworkStatus = useMemo(
		() =>
			isGetexInjected
				? injectedData?.networkStatus ?? NetworkStatus.ready
				: regDocNetworkStatusFromQuery,
		[
			isGetexInjected,
			injectedData?.networkStatus,
			regDocNetworkStatusFromQuery,
		],
	);

	return {
		injectedData,
		isGetexInjected,
		isRegDocLoading,
		data,
		regDocNetworkStatus,
		getUpdateGetexInput(id: string, formData: any) {
			const extras = injectedData?.getexExtras;
			return {
				id,
				regulationTitle: formData.summary,
				regulationNumber: formData.name ?? '',
				regulationStatus: extras?.regulationStatus ?? '',
				vehicleCategories: extras?.vehicleCategories ?? [],
				keywords: ((formData.keywords ?? []) as Array<{name: string}>).map(
					(k: {name: string}) => k.name,
				),
				dateEffective: formData.dateEffective,
				disassemblyStatus: formData.disassemblyStatus,
				status: formData.status,
				workflow: {
					status:
						(formData.workflow?.status as any) === 'APPROVING_MODIFIED'
							? WorkflowStatus.Modified
							: (formData.workflow?.status as any),
					message: formData.workflow?.message,
					reapprovingMessage: formData.workflow?.reapprovingMessage,
					dueDate: formData.workflow?.dueDate,
					dueDateInProcess: formData.workflow?.dueDateInProcess,
					clearingTimestamp: formData.workflow?.clearingTimestamp,
					clearingWithReservationsTimestamp:
						formData.workflow?.clearingWithReservationsTimestamp,
					...mapWorkflowUserRefs(formData.workflow),
				},
			};
		},
	};
};
